import { useEffect, useState } from "react";
import { BiMinus } from "react-icons/bi";
import { Translate, withLocalize } from "react-localize-redux";
import { useOutletContext } from "react-router";
import API from "utils/API";
import Loading from "utils/components/Loading";
import { useProject } from "utils/hooks/useProject";
import SubTreeItem from "./SubTreeItem";
import InformModal from "utils/components/InformModal";

export const LEVEL_COLORS = [
  "#eaf5ff", // Level 0
  "#d6e4f0", // Level 1
  "#c8dae8", // Level 2
  "#b8d0e2", // Level 3
  "#a8c6dc", // Level 4
  "#6c757d", // Level 5+
];

const CascadingListItem = ({ data, handleDelete, translate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(data.title);
  const [setItem, setSetItem] = useState(data);
  const [isNewSubSetModalOpen, setIsNewSubSetModalOpen] = useState(false);
  const [informModalMessage, setInformModalMessage] = useState("");
  const [newSubSetItem, setNewSubSetItem] = useState({
    item: null,
    name: "",
    callback: () => {},
  });
  const [disableButtons, setDisableButtons] = useState(false);
  const [isSetUpdating, setIsSetUpdating] = useState(false);
  const { project } = useProject();
  const { onCascadingImportSuccess } = useOutletContext();

  useEffect(() => {
    setSetItem(data);
  }, [data]);

  useEffect(() => {
    if (setItem?.id) getDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setItem?.id]);

  useEffect(() => {
    if (newSubSetItem.item && !isNewSubSetModalOpen) {
      setIsNewSubSetModalOpen(true);
    }

    return () => {
      if (isNewSubSetModalOpen) {
        setIsNewSubSetModalOpen(false);
        setNewSubSetItem({
          item: null,
          name: "",
          callback: () => {},
        });
      }
    };
  }, [isNewSubSetModalOpen, newSubSetItem.item]);

  // Recursive function to transform API data into tree structure
  const transformToTreeStructure = (details, level = 0) => {
    if (!details || !Array.isArray(details)) return [];

    return details.map((detail) => ({
      id: detail.id,
      name: detail.name,
      hasDetail: detail.details && detail.details.length > 0,
      subTree: detail.details ? transformToTreeStructure(detail.details, level + 1) : [],
      set_id: setItem.id,
      level: level,
      parent_id: detail.parent_id,
      selectable: detail.selectable,
      selection_code: detail.selection_code,
      template_id: detail.template_id,
    }));
  };

  const handleUploadExcel = (e) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (file) {
      setDisableButtons(true);
      setIsSetUpdating(true);
      setInformModalMessage(
        translate("project_settings.location_sets.excel_import_in_progress"),
      );

      const formData = new FormData();
      formData.append("file", file);

      API.postFile(
        `cascading_set/${setItem.id}/import?project_id=${project?.id}`,
        formData,
      )
        .then(() => {
          getDetail();
          onCascadingImportSuccess && onCascadingImportSuccess();
        })
        .catch(() => {
          setDisableButtons(false);
          setIsSetUpdating(false);
        });
    }
  };

  const getDetail = () => {
    API.getData(`cascading_set/${setItem.id}`)
      .then(({ data: res }) => {
        const data = res.data;

        const transformedSubTree = transformToTreeStructure(data);
        const newSetItem = {
          ...setItem,
          subTree: transformedSubTree,
        };

        setSetItem(newSetItem);
        setDisableButtons(false);
        setIsSetUpdating(false);
      })
      .catch(() => {
        setDisableButtons(false);
        setIsSetUpdating(false);
      });
  };

  const downloadFile = () => {
    if (setItem?.file_path) {
      setDisableButtons(true);
      setIsSetUpdating(true);
      setInformModalMessage(
        translate("project_settings.location_sets.excel_download_in_progress"),
      );

      const a = document.createElement("a");
      a.href = setItem.file_path;
      a.download = setItem.file_path.split("/").pop();
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();

      setDisableButtons(false);
      setIsSetUpdating(false);

      setTimeout(() => {
        setInformModalMessage("");
        a.remove();
      }, 1000);
    }
  };

  return (
    <div className="CascadingListItem SetComponent w-100 d-flex flex-column">
      <div className="row">
        <div className="col-12">
          <h4 className="CascadingListItemHeader set-title d-flex justify-content-between align-items-center">
            <span className="text w-100 h-100 d-flex justify-content-between align-items-center">
              <input
                type="text"
                name="name"
                value={newTitle}
                readOnly={!isEditing}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
                className="w-100 me-4"
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </span>
            <div className="right-side d-flex justify-content-end align-items-center">
              <span
                className="add-new-button flex-shrink-0 rounded-circle bg-primary text-white"
                role="button"
                onClick={() => handleDelete(setItem.id)}
                data-is-disabled={disableButtons}
              >
                <BiMinus className="fs-3"/>
              </span>
            </div>
          </h4>
        </div>
      </div>
      <div className="row" style={{ minHeight: "100%" }}>
        <div className="col-12">
          <div className="CascadingListItemSubItems" style={{ minHeight: "236px" }}>
            {isSetUpdating ? (
              <Loading/>
            ) : (
              <div className="set-items-bg">
                {setItem.subTree.map((item) => (
                  <SubTreeItem
                    key={item.id}
                    item={item}
                    translate={translate}
                    level={item.level}
                    parentId={setItem.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <label
            htmlFor={`import-button-${setItem.id}`}
            className="import-button text-center text-uppercase w-100"
            data-is-disabled={isSetUpdating}
            onClick={setItem?.file_path ? downloadFile : () => {}}
          >
            <Translate
              id={`form_templates.cascading_lists.${
                setItem?.file_path ? `download` : `import`
              }`}
            />
          </label>
          <input
            type="file"
            name="file"
            id={`import-button-${setItem.id}`}
            className="d-none"
            onChange={handleUploadExcel}
            disabled={isSetUpdating || setItem?.file_path}
            accept=".xlsx, .xls, .csv, .xlsm"
          />
        </div>
      </div>

      <InformModal
        showModal={disableButtons && !!informModalMessage}
        message={informModalMessage}
      />
    </div>
  );
};

export default withLocalize(CascadingListItem);